// src/pages/Dashboard.js
import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import API from "../api";
import FeaturedMatch from "../components/FeaturedMatch";
import MatchdayRecapCard from "../components/MatchdayRecapCard";
import YourEdgeCard from "../components/YourEdgeCard";
import GroupsCard from "../components/GroupsCard";
import { usePageTitle } from "../hooks/usePageTitle";
import Card from "../components/ui/Card";
import PageShell from "../components/ui/PageShell";
import ChartNumbers from "../components/ui/ChartNumbers";
import MatchRow, { PointsPill, pointsTone } from "../components/ui/MatchRow";
import TeamRecentMatches from "../components/TeamRecentMatches";
import FixtureCloseCountdown from "../components/FixtureCloseCountdown";
import LoadingSpinner from "../components/LoadingSpinner";
import { UserContext } from "../context/UserContext";
import { CURRENT_SEASON, formatSeasonShortLabel } from "../constants/season";
import {
  COMPETITION_OPTIONS,
  getEnabledCompetitionIds,
  getStoredPreferredCompetitionId,
} from "../constants/competitionPrefs";
import { isFixturePredictionOpen, getFixturePredictionDeadline } from "../utils/fixtureDeadline";
import { teamLogo } from "../utils/fixtureLogo";
import { SHOW_GROUPS } from "../constants/featureFlags";
import LiveNow from "../components/LiveNow";
import { formatUpdated, isOnNow, isScoreHot, latestSync, tableLine } from "../utils/liveMatch";
import { pointsReason } from "../utils/scorePoints";
import { ScoringChips } from "../components/ScoringGuide";
import InviteFriendButton, { inviteUrl } from "../components/InviteFriendButton";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const NEXT_COUNT = 5;

const competitionLabel = (c) => COMPETITION_OPTIONS.find((o) => o.id === c)?.label || c;

const hasPick = (p) =>
  p &&
  p.predicted_home_score !== null &&
  p.predicted_home_score !== undefined &&
  p.predicted_away_score !== null &&
  p.predicted_away_score !== undefined;

const isOpenFixture = (f) => {
  const st = (f.status || "").toLowerCase();
  if (!(st === "scheduled" || st === "postponed" || st === "live")) return false;
  return isFixturePredictionOpen(f);
};

function shortDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Today ${time}`;
  return `${d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" })} ${time}`;
}

function Fact({ label, value, sub }) {
  return (
    <div className="min-w-0 px-3 py-3 md:px-4">
      <p className="text-[12px] text-muted truncate">{label}</p>
      <p className="mt-0.5 text-[22px] leading-7 font-bold text-ink tabular-nums">{value}</p>
      {sub && <p className="text-[12px] text-muted truncate">{sub}</p>}
    </div>
  );
}

function RailCard({ title, action, children }) {
  return (
    <section className="rounded-xl border border-line bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
        <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Top 3 plus the rows either side of the current user, with gaps marked. */
function leagueSnapshotRows(rows, username) {
  const list = (Array.isArray(rows) ? rows : []).filter((row) => row?.username);
  if (!list.length) return [];
  const meIdx = list.findIndex((r) => r.username === username);
  const keep = new Set([0, 1, 2].filter((i) => i < list.length));
  if (meIdx >= 0) [meIdx - 1, meIdx, meIdx + 1].forEach((i) => i >= 0 && i < list.length && keep.add(i));
  const out = [];
  let prev = -1;
  [...keep]
    .sort((a, b) => a - b)
    .forEach((i) => {
      const row = list[i];
      if (!row) return;
      if (prev >= 0 && i > prev + 1) out.push({ gap: true, key: `gap-${i}` });
      out.push({
        key: row.username,
        username: row.username,
        rank: row.rank ?? i + 1,
        points: row.points ?? row.score ?? 0,
        me: i === meIdx,
      });
      prev = i;
    });
  return out;
}

function SectionTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between mb-2 px-1">
      <h2 className="text-[15px] font-semibold text-ink">{children}</h2>
      {action}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useContext(UserContext);
  usePageTitle("Dashboard");
  const [statsOpen, setStatsOpen] = useState(false);
  const [analyticsView, setAnalyticsView] = useState("accuracy");

  const seasonParams = { season: CURRENT_SEASON };
  const enabledCompetitionIds = useMemo(
    () => getEnabledCompetitionIds(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.enabled_competitions, user?.id]
  );
  const primaryCompetition = useMemo(() => {
    const preferred = getStoredPreferredCompetitionId();
    if (preferred && enabledCompetitionIds.includes(preferred)) return preferred;
    return enabledCompetitionIds[0] || "premier_league";
  }, [enabledCompetitionIds]);

  // ─── Always-on data ───────────────────────────────────────
  const { data: dashboardFixtures = [], isLoading: loadingFixtures } = useQuery({
    queryKey: ["dashboardFixtures", CURRENT_SEASON, "all"],
    queryFn: () => API.get("/gameweeks/all-fixtures", { params: seasonParams }).then((res) => res.data || []),
    staleTime: 30 * 1000,
    refetchInterval: (query) => (isScoreHot(query.state.data) ? 60 * 1000 : 5 * 60 * 1000),
  });
  const scoresHot = isScoreHot(dashboardFixtures);

  const { data: myPredictions = [] } = useQuery({
    queryKey: ["dashboardMyPredictions", CURRENT_SEASON, enabledCompetitionIds.join(",")],
    enabled: enabledCompetitionIds.length > 0,
    queryFn: async () => {
      const batches = await Promise.all(
        enabledCompetitionIds.map((competition) =>
          API.get("/predictions/my-predictions", { params: { competition } }).then((res) => res.data || [])
        )
      );
      return batches.flat();
    },
    staleTime: 30 * 1000,
    refetchInterval: scoresHot ? 60 * 1000 : false,
  });

  const { data: youSummary } = useQuery({
    queryKey: ["dashboardYouSummary", CURRENT_SEASON, user?.username, primaryCompetition],
    enabled: !!user?.username,
    queryFn: async () => {
      const { data: leaderboard } = await API.get("/leaderboard", {
        params: { competition: primaryCompetition, season: CURRENT_SEASON },
      });
      const rows = Array.isArray(leaderboard) ? leaderboard.filter((row) => row?.username) : [];
      const entry = rows.find((row) => row.username === user.username);
      return { rank: entry?.rank ?? null, seasonPoints: entry?.points ?? 0, players: rows.length, rows };
    },
    staleTime: 30 * 1000,
    refetchInterval: scoresHot ? 60 * 1000 : false,
  });

  const { data: formSummary } = useQuery({
    queryKey: ["formSummary", "all", CURRENT_SEASON],
    queryFn: () =>
      API.get("/predictions/form-summary", { params: { competition: "all", ...seasonParams } }).then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: recentResults = [] } = useQuery({
    queryKey: ["recentResults", "all", CURRENT_SEASON],
    queryFn: () =>
      API.get("/predictions/recent-results", { params: { competition: "all", limit: 8, ...seasonParams } }).then(
        (res) => res.data
      ),
    staleTime: 30 * 1000,
    refetchInterval: scoresHot ? 60 * 1000 : false,
  });

  // ─── Stats (loaded when opened) ───────────────────────────
  const { data: analytics = { premier_league: null, wildcard: null, ucl: null }, isLoading: isLoadingAnalytics } =
    useQuery({
      queryKey: ["dashboardAnalytics", CURRENT_SEASON],
      enabled: statsOpen,
      queryFn: async () => {
        const [pl, wc, ucl] = await Promise.all(
          ["premier_league", "wildcard", "ucl"].map((competition) =>
            API.get("/predictions/analytics", { params: { competition, ...seasonParams } })
          )
        );
        return { premier_league: pl.data, wildcard: wc.data, ucl: ucl.data };
      },
      staleTime: 5 * 60 * 1000,
    });

  const { data: weeklyWinners = [] } = useQuery({
    queryKey: ["monthlyWinners", CURRENT_SEASON],
    enabled: statsOpen,
    queryFn: () =>
      API.get("/predictions/weekly-winners", { params: { window: "month", ...seasonParams } }).then((res) => res.data),
    retry: 1,
  });

  // ─── Derived ──────────────────────────────────────────────
  const pickByFixture = useMemo(() => {
    const map = new Map();
    myPredictions.forEach((p) => {
      if (hasPick(p)) map.set(p.fixture_id, p);
    });
    return map;
  }, [myPredictions]);

  const enabledFixtures = useMemo(
    () => (dashboardFixtures || []).filter((f) => enabledCompetitionIds.includes(f.competition)),
    [dashboardFixtures, enabledCompetitionIds]
  );

  const openFixtures = useMemo(
    () => enabledFixtures.filter((f) => isOpenFixture(f)),
    [enabledFixtures]
  );

  const openUnpicked = useMemo(
    () => openFixtures.filter((f) => !pickByFixture.has(f.id)),
    [openFixtures, pickByFixture]
  );

  const nextDeadline = useMemo(
    () =>
      openFixtures
        .map((f) => ({ f, d: getFixturePredictionDeadline(f) }))
        .filter((x) => x.d && !Number.isNaN(x.d.getTime()))
        .sort((a, b) => a.d - b.d)[0] || null,
    [openFixtures]
  );

  const nextMatches = useMemo(() => {
    const now = Date.now();
    return enabledFixtures
      .filter((f) => {
        const st = (f.status || "").toLowerCase();
        return st === "scheduled" && f.match_date && new Date(f.match_date).getTime() > now;
      })
      .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
      .slice(0, NEXT_COUNT);
  }, [enabledFixtures]);

  const liveMatches = useMemo(
    () =>
      enabledFixtures
        .filter((fixture) => isOnNow(fixture))
        .sort((a, b) => new Date(a.match_date) - new Date(b.match_date)),
    [enabledFixtures]
  );
  const liveCompetition = useMemo(() => {
    const ids = [...new Set(liveMatches.map((fixture) => fixture.competition))];
    return ids.length === 1 ? ids[0] : null;
  }, [liveMatches]);
  const { data: liveBoard } = useQuery({
    queryKey: ["dashboardYouSummary", CURRENT_SEASON, user?.username, liveCompetition],
    enabled: !!user?.username && !!liveCompetition && liveCompetition !== primaryCompetition,
    queryFn: async () => {
      const { data: leaderboard } = await API.get("/leaderboard", {
        params: { competition: liveCompetition, season: CURRENT_SEASON },
      });
      const rows = Array.isArray(leaderboard) ? leaderboard.filter((row) => row?.username) : [];
      const entry = rows.find((row) => row.username === user.username);
      return { rank: entry?.rank ?? null, seasonPoints: entry?.points ?? 0, players: rows.length, rows };
    },
    staleTime: 30 * 1000,
    refetchInterval: scoresHot ? 60 * 1000 : false,
  });
  const liveTable = liveCompetition
    ? liveCompetition === primaryCompetition
      ? youSummary
      : liveBoard
    : null;

  const predictCompetition = nextDeadline?.f?.competition || primaryCompetition;
  const deadlineSoon = nextDeadline && nextDeadline.d.getTime() - Date.now() < 2 * 60 * 60 * 1000;
  const lockLine = nextDeadline ? (
    <Link
      to={`/predictions?competition=${encodeURIComponent(nextDeadline.f.competition)}&fixture=${nextDeadline.f.id}`}
      className={`mt-0.5 block text-meta hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
        deadlineSoon ? "text-amber-800 font-semibold" : "text-muted"
      }`}
    >
      Next lock {shortDate(nextDeadline.d)} · {nextDeadline.f.home_team} v {nextDeadline.f.away_team}
    </Link>
  ) : null;

  // ─── Chart ────────────────────────────────────────────────
  const isAccuracyView = analyticsView === "accuracy";
  const formatMonthLabel = (label) => {
    if (typeof label === "string" && /^\d{4}-\d{2}$/.test(label)) {
      const d = new Date(`${label}-01T00:00:00Z`);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString("en-US", { month: "short" });
    }
    return label || "";
  };
  const series = (a) => (isAccuracyView ? a?.accuracy_history : a?.points_history) || [];
  const chartData = {
    labels: series(analytics.premier_league).map((e) => formatMonthLabel(e.label)),
    datasets: [
      { label: "Premier League", data: series(analytics.premier_league).map((e) => e.value), borderColor: "#1e3a8a" },
      { label: "Wildcard", data: series(analytics.wildcard).map((e) => e.value), borderColor: "#9ca3af" },
      { label: "UCL", data: series(analytics.ucl).map((e) => e.value), borderColor: "#d97706" },
    ].map((d) => ({ ...d, tension: 0.3, pointRadius: 2, borderWidth: 2 })),
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: "bottom", labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}${isAccuracyView ? "%" : " pts"}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: isAccuracyView
        ? { min: 0, max: 100, ticks: { stepSize: 25, callback: (v) => `${v}%` }, grid: { color: "#f3f4f6" } }
        : { min: 0, suggestedMax: 30, ticks: { stepSize: 10 }, grid: { color: "#f3f4f6" } },
    },
  };

  const hasAnalytics = analytics.premier_league || analytics.wildcard || analytics.ucl;

  const formTiles = [
    ["Exact streak", formSummary?.perfectStreak ?? 0],
    ["Scoring streak", formSummary?.correctStreak ?? 0],
    ["Last 30 days", `${formSummary?.last30 ?? 0} pts`],
  ];
  const snapshot = leagueSnapshotRows(youSummary?.rows, user?.username);
  const firstSession = recentResults.length === 0;
  const playerCount = youSummary?.players || 0;
  const rankSub = playerCount
    ? playerCount < 12
      ? `${playerCount} playing · it's early`
      : `of ${playerCount} · ${competitionLabel(primaryCompetition)}`
    : competitionLabel(primaryCompetition);

  const rail = (
    <>
      {snapshot.length > 0 && (
        <div className="hidden lg:block">
          <RailCard
            title={`${competitionLabel(primaryCompetition)} table`}
            action={
              <Link to="/leaderboard" className="text-meta font-semibold text-brand hover:underline">
                Full table
              </Link>
            }
          >
            <ol className="text-sm">
              {snapshot.map((r) =>
                r.gap ? (
                  <li key={r.key} aria-hidden className="px-4 py-1 text-center text-muted leading-none">
                    ⋯
                  </li>
                ) : (
                  <li
                    key={r.key}
                    className={`flex items-center gap-3 px-4 h-10 ${
                      r.me ? "bg-brand-soft shadow-[inset_3px_0_0_#1e3a8a] font-semibold" : ""
                    }`}
                  >
                    <span className="w-6 text-right tabular-nums text-muted">{r.rank}</span>
                    <span className="flex-1 min-w-0 truncate text-ink">{r.me ? "You" : r.username}</span>
                    <span className="tabular-nums text-ink">{r.points ?? 0}</span>
                  </li>
                )
              )}
            </ol>
          </RailCard>
        </div>
      )}

      <div className="hidden lg:block">
        <RailCard title="Your form">
          <div className="grid grid-cols-3 gap-2 p-4 text-center">
            {formTiles.map(([label, value]) => (
              <div key={label} className="rounded-lg bg-gray-50 px-2 py-2">
                <p className="text-[17px] font-bold tabular-nums text-ink">{value}</p>
                <p className="text-[11px] text-muted">{label}</p>
              </div>
            ))}
          </div>
        </RailCard>
      </div>

      <FeaturedMatch />
      {SHOW_GROUPS && <GroupsCard />}
    </>
  );

  return (
    <div className="min-h-screen bg-paper text-ink">
      <PageShell
        rail={rail}
        header={
          <div>
            <h1 className="text-[22px] md:text-[28px] leading-tight font-bold text-ink">
              {user?.username ? `Hi, ${user.username}` : "Home"}
            </h1>
            <p className="mt-1 text-meta text-muted">
              {firstSession
                ? "You're early — pick tonight, then bring a friend."
                : `${formatSeasonShortLabel(CURRENT_SEASON)} season`}
            </p>
          </div>
        }
      >

        <LiveNow
          matches={liveMatches}
          pickFor={(match) => pickByFixture.get(match.id)}
          logoFor={(match, side) =>
            teamLogo(
              side === "home" ? match.home_team : match.away_team,
              match.competition,
              side === "home" ? match.home_crest_url : match.away_crest_url
            )
          }
          metaFor={(match) => competitionLabel(match.competition)}
          updated={formatUpdated(latestSync(liveMatches))}
          tableText={tableLine(liveTable?.rows, user?.username)}
        />

        {/* Summary: what needs doing */}
        <section className="rounded-xl border border-line bg-white shadow-sm overflow-hidden">
          <div className="p-4 md:p-5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              {loadingFixtures ? (
                <p className="text-[17px] font-semibold text-muted">Checking open matches…</p>
              ) : openUnpicked.length > 0 ? (
                <>
                  <p className="text-[17px] font-semibold text-ink">
                    {firstSession
                      ? `You're in — ${openUnpicked.length} ${openUnpicked.length === 1 ? "match" : "matches"} still to pick`
                      : `${openUnpicked.length} ${openUnpicked.length === 1 ? "match" : "matches"} still to pick`}
                  </p>
                  {lockLine}
                </>
              ) : (
                <>
                  <p className="text-[17px] font-semibold text-ink">
                    {firstSession ? "You're in. Every open match has your score." : "You're all picked"}
                  </p>
                  {lockLine || (
                    <p className="mt-0.5 text-meta text-muted">Every open match has your score. You can still change them.</p>
                  )}
                </>
              )}
              {firstSession && <ScoringChips className="mt-2" />}
            </div>
            <Link
              to={`/predictions?competition=${predictCompetition}`}
              className={`shrink-0 inline-flex items-center h-11 px-4 rounded-lg text-[15px] font-semibold ${
                openUnpicked.length > 0
                  ? "bg-brand text-white hover:bg-brand-hover"
                  : "bg-white text-ink border border-line hover:border-gray-400"
              }`}
            >
              {openUnpicked.length > 0 ? "Predict" : "Review"}
            </Link>
          </div>

          <p className="border-t border-line px-4 py-3 text-meta text-muted md:px-5">
            <InviteFriendButton className="font-semibold text-brand hover:underline" />
            {" · "}
            they pick the same matches as you.
            <span className="mt-1 block select-all text-ink">{inviteUrl()}</span>
          </p>

          <div className="grid grid-cols-3 divide-x divide-line border-t border-line">
            <Fact
              label="Rank"
              value={youSummary?.rank ? `#${youSummary.rank}` : "—"}
              sub={rankSub}
            />
            <Fact label="Season points" value={youSummary?.seasonPoints ?? 0} />
            <Fact
              label="Last 7 days"
              value={formSummary?.last7 ?? 0}
              sub={formSummary ? `prev 7: ${formSummary.prev7 ?? 0}` : null}
            />
          </div>
        </section>

        {/* Next matches */}
        <section aria-label="Next matches">
          <SectionTitle
            action={
              <Link to="/fixtures" className="text-meta font-semibold text-brand hover:underline">
                All fixtures
              </Link>
            }
          >
            Next matches
          </SectionTitle>
          {loadingFixtures ? (
            <div className="py-6">
              <LoadingSpinner message="Loading fixtures..." />
            </div>
          ) : nextMatches.length === 0 ? (
            <Card>
              <p className="text-center text-muted">No upcoming matches scheduled.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {nextMatches.map((f) => {
                const pick = pickByFixture.get(f.id);
                const open = isOpenFixture(f);
                return (
                  <MatchRow
                    key={f.id}
                    competition={f.competition}
                    to={`/predictions?competition=${f.competition}&fixture=${f.id}`}
                    home={{ name: f.home_team, logo: teamLogo(f.home_team, f.competition, f.home_crest_url) }}
                    away={{ name: f.away_team, logo: teamLogo(f.away_team, f.competition, f.away_crest_url) }}
                    homeExtra={
                      f.competition !== "world_cup" ? (
                        <TeamRecentMatches teamName={f.home_team} competition={f.competition} />
                      ) : null
                    }
                    awayExtra={
                      f.competition !== "world_cup" ? (
                        <TeamRecentMatches teamName={f.away_team} competition={f.competition} />
                      ) : null
                    }
                    meta={`${competitionLabel(f.competition)} · ${shortDate(f.match_date)}`}
                    metaRight={
                      pick ? (
                        <span className="text-[12px] font-semibold text-gray-700">Picked</span>
                      ) : open ? (
                        <FixtureCloseCountdown fixture={f} />
                      ) : null
                    }
                    center={
                      pick ? (
                        <div className="text-center">
                          <p className="text-score font-bold tabular-nums text-ink">
                            {pick.predicted_home_score}–{pick.predicted_away_score}
                          </p>
                          <p className="text-[11px] text-muted">your pick</p>
                        </div>
                      ) : (
                        <span className="text-meta font-semibold text-brand">{open ? "Pick" : "–"}</span>
                      )
                    }
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* Recent results */}
        <section aria-label="Your recent results">
          <SectionTitle>Your recent results</SectionTitle>
          {recentResults.length === 0 ? (
            <Card>
              <p className="text-center text-muted text-sm">
                Your first full-time result lands here. Pick tonight and the points show after the whistle.
              </p>
            </Card>
          ) : (
            <div className="space-y-2">
              {recentResults.slice(0, 5).map((m) => {
                const reason = pointsReason(m.points_awarded);
                return (
                  <MatchRow
                    key={m.fixture_id}
                    competition={m.competition}
                    tone={pointsTone(m.points_awarded)}
                    home={{ name: m.home_team, logo: teamLogo(m.home_team, m.competition) }}
                    away={{ name: m.away_team, logo: teamLogo(m.away_team, m.competition) }}
                    meta={`${competitionLabel(m.competition)} · ${new Date(m.match_date).toLocaleDateString([], {
                      day: "numeric",
                      month: "short",
                    })}`}
                    metaRight={<PointsPill points={m.points_awarded ?? 0} />}
                    center={
                      <div className="text-center">
                        <p className="text-score font-bold tabular-nums text-ink">
                          {m.home_score}–{m.away_score}
                        </p>
                        <p className="text-[11px] text-muted tabular-nums">
                          you {m.predicted_home_score}–{m.predicted_away_score}
                        </p>
                        {reason && <p className="text-[11px] font-medium text-muted">{reason}</p>}
                      </div>
                    }
                  />
                );
              })}
            </div>
          )}
        </section>

        <MatchdayRecapCard competition={primaryCompetition} refreshMs={scoresHot ? 60 * 1000 : 0} />
        <YourEdgeCard />

        {/* Stats disclosure */}
        <section className="rounded-xl border border-line bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setStatsOpen((o) => !o)}
            aria-expanded={statsOpen}
            className="w-full flex items-center justify-between gap-3 px-4 md:px-5 h-14 text-left"
          >
            <span>
              <span className="block text-[15px] font-semibold text-ink">Stats and trends</span>
              <span className="block text-[12px] text-muted">Form, accuracy, monthly winners</span>
            </span>
            <ChevronDownIcon
              aria-hidden
              className={`w-5 h-5 text-gray-500 transition-transform ${statsOpen ? "rotate-180" : ""}`}
            />
          </button>

          {statsOpen && (
            <div className="border-t border-line p-4 md:p-5 space-y-6">
              <div className="lg:hidden">
                <h3 className="text-meta font-semibold text-gray-700 mb-2">Form</h3>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {formTiles.map(([label, value]) => (
                    <div key={label} className="rounded-lg bg-gray-50 px-2 py-2">
                      <p className="text-[17px] font-bold tabular-nums text-ink">{value}</p>
                      <p className="text-[11px] text-muted">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-meta font-semibold text-gray-700">
                    {isAccuracyView ? "Accuracy by month" : "Points by month"}
                  </h3>
                  <div className="inline-flex rounded-full border border-line p-0.5 text-[12px] font-semibold">
                    {[
                      ["accuracy", "Accuracy"],
                      ["points", "Points"],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setAnalyticsView(key)}
                        className={`px-2.5 h-7 rounded-full ${
                          analyticsView === key ? "bg-brand text-white" : "text-gray-600 hover:text-ink"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {isLoadingAnalytics ? (
                  <div className="h-56 flex items-center justify-center">
                    <LoadingSpinner message="Loading analytics..." />
                  </div>
                ) : hasAnalytics ? (
                  <>
                    <div className="h-56 md:h-64">
                      <Line data={chartData} options={chartOptions} aria-label={isAccuracyView ? "Accuracy by month" : "Points by month"} />
                    </div>
                    <ChartNumbers
                      caption={isAccuracyView ? "Accuracy by month" : "Points by month"}
                      columns={["Month", "Premier League", "Wildcard", "UCL"]}
                      rows={chartData.labels.map((label, index) => [
                        label || "—",
                        chartData.datasets[0]?.data[index] ?? "—",
                        chartData.datasets[1]?.data[index] ?? "—",
                        chartData.datasets[2]?.data[index] ?? "—",
                      ])}
                    />
                  </>
                ) : (
                  <p className="text-muted text-sm py-6 text-center">No analytics yet.</p>
                )}
              </div>

              <div>
                <h3 className="text-meta font-semibold text-gray-700 mb-2">Monthly winners</h3>
                {weeklyWinners.length === 0 ? (
                  <p className="text-muted text-sm">No winners yet.</p>
                ) : (
                  <ul className="divide-y divide-line text-sm">
                    {weeklyWinners.slice(0, 5).map((w) => (
                      <li
                        key={`${w.competition}-${w.week_number ?? w.stage_name}-${w.username}`}
                        className="py-2 flex items-center justify-between gap-3"
                      >
                        <span className="min-w-0">
                          <span className="font-semibold text-ink">{w.username}</span>
                          <span className="block text-[12px] text-muted truncate">
                            {competitionLabel(w.competition)} · {w.window_label || "Last 7 days"}
                          </span>
                        </span>
                        <span className="tabular-nums font-semibold text-ink shrink-0">{w.total_points ?? 0} pts</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </section>
      </PageShell>
    </div>
  );
}
